import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useState, type ReactElement } from "react";
import { ContentHeader, Field, MoneyInput, QueryStatus, StatusBadge, useToast } from "@cx/ui";
import type { ContractFormOptions, ContractView } from "@cx/contracts";
import {
  CONTRACT_PERIODICITIES,
  CONTRACT_STATUSES,
  PERIOD_PRINTS,
  formatBRL,
  formatCompetence,
  formatDateBR,
  todayInSaoPaulo,
} from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { ContactPicker } from "../finance/ContactPicker";
import { TITLE_STATUS_LABEL } from "../finance/labels";
import { ServiceFields } from "./ServiceFields";
import {
  CHARGE_KIND_LABEL,
  CONTRACT_STATUS_LABEL,
  CONTRACT_STATUS_TONE,
  DUE_OFFSET_LABEL,
  INVOICE_MODE_LABEL,
  NFSE_STATUS_LABEL,
  NFSE_STATUS_TONE,
  PERIODICITY_LABEL,
  PERIOD_PRINT_LABEL,
  contractBody,
  contractFormOf,
  emptyContractForm,
  type ContractForm,
} from "./labels";

/**
 * Serviços › Contrato (RF-SER-01): cadastro (cliente, "cobrar de", valor, vencimento, periodicidade, valor total
 * previsto, NFS-e e cobrança automática) e, na edição, as cobranças geradas com conta a receber e NFS-e.
 */
export function ContractFormPage(): ReactElement {
  const { id } = useParams({ strict: false }) as { id?: string };
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const view = useQuery({
    queryKey: ["contracts", companyId, "view", id],
    enabled: !!id,
    refetchOnMount: "always",
    queryFn: () => api<ContractView>(`/contracts/${id}`),
  });
  const canSave = id ? can(perms, "contracts", "update") : can(perms, "contracts", "create");
  const options = useQuery({
    queryKey: ["contracts", companyId, "form-options"],
    enabled: canSave,
    queryFn: () => api<ContractFormOptions>("/contracts/form-options"),
  });
  const [f, setF] = useState<ContractForm>(() => emptyContractForm(todayInSaoPaulo()));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [idemKey] = useState(() => crypto.randomUUID());
  useEffect(() => {
    if (view.data) setF(contractFormOf(view.data));
  }, [view.data]);
  const set = <K extends keyof ContractForm>(k: K, v: ContractForm[K]) => setF((x) => ({ ...x, [k]: v }));

  const save = useMutation({
    mutationFn: () => {
      const body = JSON.stringify(contractBody(f));
      return id
        ? api<ContractView>(`/contracts/${id}`, { method: "PUT", body })
        : api<ContractView>("/contracts", { method: "POST", body, headers: { "Idempotency-Key": idemKey } });
    },
    onSuccess: (c) => {
      setErrors({});
      toast("success", `Contrato nº ${c.number} salvo`);
      qc.invalidateQueries({ queryKey: ["contracts", companyId] });
      if (!id) navigate({ to: "/servicos/contratos/$id", params: { id: c.id } });
      else qc.setQueryData(["contracts", companyId, "view", id], c);
    },
    onError: (e) => {
      const fields = e instanceof ApiFailure && e.fields ? { ...e.fields } : {};
      if (e instanceof ApiFailure && e.code === "number_in_use") fields.number = e.message;
      if (e instanceof ApiFailure && e.code === "invalid_contact") fields.contactId = e.message;
      setErrors(fields);
      toast("danger", errorMessage(e));
    },
  });

  const c = view.data;
  const o = options.data;
  const title = c ? `Contrato nº ${c.number}` : id ? "Contrato" : "Novo contrato";
  const select = (
    k: "paymentMethodId" | "financialAccountId" | "financialCategoryId" | "sellerId",
    list: { id: string; name: string }[] | undefined,
    current: { id: string; name: string } | null | undefined,
    empty: string,
  ) => {
    const all = list ?? [];
    const items = current && !all.some((x) => x.id === current.id) ? [current, ...all] : all;
    return (
      <select
        className="form-select"
        value={f[k]}
        disabled={!canSave}
        onChange={(e) => set(k, e.target.value)}
      >
        <option value="">{empty}</option>
        {items.map((x) => (
          <option key={x.id} value={x.id}>
            {x.name}
          </option>
        ))}
      </select>
    );
  };

  return (
    <>
      <ContentHeader
        title={title}
        breadcrumb={["Serviços", "Contratos", c ? String(c.number) : id ? "" : "Novo"]}
        actions={
          <Link to="/servicos/contratos" className="btn btn-outline-secondary">
            Voltar
          </Link>
        }
      />
      <QueryStatus loading={!!id && view.isLoading} error={view.error} />
      {(!id || c) && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSave) save.mutate();
          }}
        >
          <div className="box card p-3 mb-3">
            {c && (
              <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                <StatusBadge tone={CONTRACT_STATUS_TONE[c.status]}>
                  {CONTRACT_STATUS_LABEL[c.status]}
                </StatusBadge>
                <span className="text-muted small" data-testid="contract-billed">
                  Já cobrado: {formatBRL(c.billedCents)}
                  {c.expectedTotalCents !== null && ` de ${formatBRL(c.expectedTotalCents)} previstos`}
                </span>
              </div>
            )}
            <div className="row">
              <div className="col-md-6">
                <ContactPicker
                  label="Cliente"
                  inputId="contract-contact"
                  value={f.contact}
                  error={errors.contactId}
                  onChange={(v) => set("contact", v)}
                />
              </div>
              <div className="col-md-6">
                <ContactPicker
                  label="Cobrar de (se outro)"
                  inputId="contract-bill-to"
                  required={false}
                  value={f.billTo}
                  error={errors.billToContactId}
                  onChange={(v) => set("billTo", v)}
                />
              </div>
            </div>
            <div className="row">
              <Field label="Número" className="col-md-2" error={errors.number} hint="Em branco = próximo">
                <input
                  inputMode="numeric"
                  value={f.number}
                  disabled={!canSave}
                  onChange={(e) => set("number", e.target.value.replace(/\D/g, "").slice(0, 9))}
                />
              </Field>
              <Field label="Descrição" className="col-md-6" error={errors.description}>
                <input
                  value={f.description}
                  maxLength={255}
                  disabled={!canSave}
                  onChange={(e) => set("description", e.target.value)}
                />
              </Field>
              <Field label="Situação" required className="col-md-4" error={errors.status}>
                <select
                  className="form-select"
                  value={f.status}
                  disabled={!canSave}
                  onChange={(e) => set("status", e.target.value as ContractForm["status"])}
                >
                  {CONTRACT_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {CONTRACT_STATUS_LABEL[s]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Data do contrato" required className="col-md-3" error={errors.contractDate}>
                <input
                  type="date"
                  value={f.contractDate}
                  disabled={!canSave}
                  onChange={(e) => set("contractDate", e.target.value)}
                />
              </Field>
              <Field
                label="Data base"
                className="col-md-3"
                error={errors.baseDate}
                hint="Início das cobranças (em branco = data do contrato)"
              >
                <input
                  type="date"
                  value={f.baseDate}
                  disabled={!canSave}
                  onChange={(e) => set("baseDate", e.target.value)}
                />
              </Field>
              <Field label="Valor" required className="col-md-3" error={errors.amountCents}>
                <MoneyInput
                  value={f.amountCents}
                  disabled={!canSave}
                  onChange={(v) => set("amountCents", v)}
                />
              </Field>
              <Field
                label="Valor total previsto"
                className="col-md-3"
                error={errors.expectedTotalCents}
                hint="Atingido, o contrato para de cobrar"
              >
                <MoneyInput
                  value={f.expectedTotalCents}
                  disabled={!canSave}
                  onChange={(v) => set("expectedTotalCents", v)}
                />
              </Field>
              <Field label="Periodicidade" required className="col-md-3" error={errors.periodicity}>
                <select
                  className="form-select"
                  value={f.periodicity}
                  disabled={!canSave}
                  onChange={(e) => set("periodicity", e.target.value as ContractForm["periodicity"])}
                >
                  {CONTRACT_PERIODICITIES.map((p) => (
                    <option key={p} value={p}>
                      {PERIODICITY_LABEL[p]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Dia do vencimento" required className="col-md-2" error={errors.dueDay}>
                <input
                  inputMode="numeric"
                  value={f.dueDay}
                  disabled={!canSave}
                  onChange={(e) => set("dueDay", e.target.value.replace(/\D/g, "").slice(0, 2))}
                />
              </Field>
              <Field label="Mês do vencimento" className="col-md-3" error={errors.dueMonthOffset}>
                <select
                  className="form-select"
                  value={f.dueMonthOffset}
                  disabled={!canSave}
                  onChange={(e) => set("dueMonthOffset", Number(e.target.value) as 0 | 1 | 2)}
                >
                  {([0, 1, 2] as const).map((k) => (
                    <option key={k} value={k}>
                      {DUE_OFFSET_LABEL[k]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Imprimir período" className="col-md-4" error={errors.periodPrint}>
                <select
                  className="form-select"
                  value={f.periodPrint}
                  disabled={!canSave}
                  onChange={(e) => set("periodPrint", e.target.value as ContractForm["periodPrint"])}
                >
                  {PERIOD_PRINTS.map((p) => (
                    <option key={p} value={p}>
                      {PERIOD_PRINT_LABEL[p]}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          </div>
          <div className="box card p-3 mb-3">
            <h2 className="h6">Cobrança e financeiro</h2>
            <div className="row">
              <Field label="Cobrança automática" className="col-md-4" error={errors.chargeKind}>
                <select
                  className="form-select"
                  value={f.chargeKind}
                  disabled={!canSave}
                  onChange={(e) => set("chargeKind", e.target.value as ContractForm["chargeKind"])}
                >
                  {(["none", "pix"] as const).map((k) => (
                    <option key={k} value={k}>
                      {CHARGE_KIND_LABEL[k]}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Forma de pagamento" className="col-md-4" error={errors.paymentMethodId}>
                {select("paymentMethodId", o?.paymentMethods, c?.paymentMethod, "Não informada")}
              </Field>
              <Field label="Conta financeira" className="col-md-4" error={errors.financialAccountId}>
                {select("financialAccountId", o?.financialAccounts, c?.financialAccount, "Padrão")}
              </Field>
              <Field label="Categoria" className="col-md-4" error={errors.financialCategoryId}>
                {select(
                  "financialCategoryId",
                  o?.financialCategories,
                  c?.financialCategory,
                  "Vendas de serviços",
                )}
              </Field>
              <Field label="Vendedor" className="col-md-4" error={errors.sellerId}>
                {select("sellerId", o?.sellers, c?.seller, "Nenhum")}
              </Field>
            </div>
          </div>
          <div className="box card p-3 mb-3">
            <h2 className="h6">Nota fiscal de serviço</h2>
            <div className="row">
              <Field label="NFS-e" className="col-md-4" error={errors.invoiceMode}>
                <select
                  className="form-select"
                  value={f.invoiceMode}
                  disabled={!canSave}
                  onChange={(e) => set("invoiceMode", e.target.value as ContractForm["invoiceMode"])}
                >
                  {(["none", "on_charge"] as const).map((k) => (
                    <option key={k} value={k}>
                      {INVOICE_MODE_LABEL[k]}
                    </option>
                  ))}
                </select>
              </Field>
              {f.invoiceMode === "on_charge" && (
                <div className="col-md-8 mb-3 d-flex align-items-end">
                  <div className="form-check">
                    <input
                      id="contract-own-service"
                      type="checkbox"
                      className="form-check-input"
                      checked={f.ownService}
                      disabled={!canSave}
                      onChange={(e) => set("ownService", e.target.checked)}
                    />
                    <label htmlFor="contract-own-service" className="form-check-label">
                      Serviço próprio deste contrato (senão o serviço padrão da configuração da NFS-e)
                    </label>
                  </div>
                </div>
              )}
            </div>
            {f.invoiceMode === "on_charge" && f.ownService && (
              <ServiceFields
                value={f.service}
                errors={errors}
                disabled={!canSave}
                onChange={(v) => set("service", v)}
              />
            )}
          </div>
          <div className="box card p-3 mb-3">
            <Field label="Observações" className="col-12" error={errors.notes}>
              <textarea
                rows={2}
                value={f.notes}
                maxLength={2000}
                disabled={!canSave}
                onChange={(e) => set("notes", e.target.value)}
              />
            </Field>
            {canSave && (
              <div>
                <button type="submit" className="btn btn-primary" disabled={save.isPending}>
                  Salvar contrato
                </button>
              </div>
            )}
          </div>
        </form>
      )}
      {c && (
        <div className="box card mb-3">
          <h2 className="h6 p-3 pb-0">Cobranças</h2>
          {c.billings.length === 0 ? (
            <p className="text-muted px-3">
              Nenhuma cobrança ainda. Gere as cobranças do mês em{" "}
              <Link to="/servicos/cobrancas">Serviços › Cobranças de contratos</Link>.
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table align-middle mb-0" aria-label="Cobranças do contrato">
                <thead>
                  <tr>
                    <th scope="col">Competência</th>
                    <th scope="col">Vencimento</th>
                    <th scope="col" className="text-end">
                      Valor
                    </th>
                    <th scope="col">Conta a receber</th>
                    <th scope="col">NFS-e</th>
                    <th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {c.billings.map((b) => (
                    <tr key={b.id}>
                      <td>{formatCompetence(b.competence)}</td>
                      <td>{formatDateBR(b.dueDate)}</td>
                      <td className="text-end">{formatBRL(b.amountCents)}</td>
                      <td>
                        {b.title ? (
                          can(perms, "receivables", "read") ? (
                            <Link to="/financas/contas-a-receber/$id" params={{ id: b.title.id }}>
                              {TITLE_STATUS_LABEL[b.title.status]}
                            </Link>
                          ) : (
                            TITLE_STATUS_LABEL[b.title.status]
                          )
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        {b.serviceInvoice ? (
                          <Link to="/servicos/notas-de-servico/$id" params={{ id: b.serviceInvoice.id }}>
                            <StatusBadge tone={NFSE_STATUS_TONE[b.serviceInvoice.status]}>
                              {b.serviceInvoice.nfseNumber ? `nº ${b.serviceInvoice.nfseNumber} · ` : ""}
                              {NFSE_STATUS_LABEL[b.serviceInvoice.status]}
                            </StatusBadge>
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <StatusBadge tone={b.status === "active" ? "success" : "secondary"}>
                          {b.status === "active" ? "Ativa" : "Estornada"}
                        </StatusBadge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}
